// src/pages/Login.tsx
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { User, Users, School, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, quickLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as any;

  const [email, setEmail] = useState("demo@mixmind.app");
  const [password, setPassword] = useState("demo1234");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const from = location.state?.from || "/";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");

    const res = login(email, password);
    if (!res.ok) {
      setError(res.reason || "Connexion impossible (vitrine).");
      return;
    }

    navigate(from);
  };

  const handleQuick = (type: "individual" | "family" | "school") => {
    setError("");
    setInfo("");

    quickLogin(type);
    if (type === "individual") {
      setInfo("Connecté avec un compte individuel de démonstration.");
    } else if (type === "family") {
      setInfo("Connecté avec un compte famille de démonstration.");
    } else {
      setInfo("Connecté avec un compte school de démonstration.");
    }
    navigate("/");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <div className="grid gap-8 md:grid-cols-[1.1fr,0.9fr] items-start">
        {/* Colonne gauche : connexion classique */}
        <section className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-6 md:p-7">
          <h1 className="text-2xl font-semibold mb-2">Se connecter</h1>
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
            Cette page est une vitrine : aucune donnée réelle n&apos;est
            stockée. Utilise le formulaire ci-dessous ou les boutons de
            connexion rapide à droite.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 text-sm">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Adresse e-mail (vitrine)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Mot de passe (vitrine)
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
              />
            </div>

            {error && (
              <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 mt-1"
            >
              <span>Se connecter</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {info && (
            <p className="mt-4 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-lg px-3 py-2">
              {info}
            </p>
          )}

          <p className="mt-6 text-[11px] text-slate-500 dark:text-slate-400">
            Astuce : tu peux modifier librement l&apos;e-mail et le mot de
            passe, mais seuls les jeux d&apos;identifiants de démonstration
            fonctionneront : <br />
            <span className="font-mono">
              demo@mixmind.app / demo1234 – Individuel
            </span>
            ,{" "}
            <span className="font-mono">
              family@mixmind.app / family1234 – Famille
            </span>
            ,{" "}
            <span className="font-mono">
              school@mixmind.app / school1234 – School
            </span>
            .
          </p>
        </section>

        {/* Colonne droite : connexions rapides + faux "création compte" */}
        <section className="space-y-4">
          <div className="bg-slate-900 text-slate-50 rounded-2xl border border-slate-800 shadow-sm p-5 text-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">
              Connexion rapide (démo)
            </p>
            <p className="text-xs text-slate-300 mb-4">
              Ces boutons se connectent directement à des comptes de
              démonstration pré-configurés. Idéal pour explorer MixMind sans
              effort.
            </p>

            <div className="space-y-2">
              <QuickButton
                icon={<User className="w-4 h-4" />}
                label="Compte individuel démo"
                sub="Un seul profil, usage personnel."
                onClick={() => handleQuick("individual")}
              />
              <QuickButton
                icon={<Users className="w-4 h-4" />}
                label="Compte famille démo"
                sub="Parents + enfants avec profils séparés."
                onClick={() => handleQuick("family")}
              />
              <QuickButton
                icon={<School className="w-4 h-4" />}
                label="Compte school démo"
                sub="Enseignant + élève, usage en classe."
                onClick={() => handleQuick("school")}
              />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-5 text-sm">
            <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
              Faux formulaire de création (vitrine)
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300 mb-3 leading-relaxed">
              Dans une vraie version, tu pourrais créer un compte avec ton
              adresse e-mail. Ici, la création de compte déclenche simplement
              une connexion sur un compte de démonstration correspondant.
            </p>

            <div className="space-y-2 text-xs">
              <FakeCreateCard
                title="Créer un compte individuel"
                description="Pour une personne seule qui souhaite explorer MixMind avec un seul profil."
                onClick={() => handleQuick("individual")}
              />
              <FakeCreateCard
                title="Créer un compte famille"
                description="Pour un foyer avec parents et enfants, chacun son profil et son espace."
                onClick={() => handleQuick("family")}
              />
              <FakeCreateCard
                title="Créer un compte school"
                description="Pour un enseignant et sa classe, en usage collectif."
                onClick={() => handleQuick("school")}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function QuickButton({
  icon,
  label,
  sub,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 rounded-xl bg-slate-800 hover:bg-slate-700 px-3 py-2.5 text-left transition-colors"
    >
      <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <p className="text-xs font-semibold">{label}</p>
        <p className="text-[11px] text-slate-400">{sub}</p>
      </div>
    </button>
  );
}

function FakeCreateCard({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-3">
      <p className="text-xs font-semibold mb-1">{title}</p>
      <p className="text-[11px] text-slate-600 dark:text-slate-300 mb-2">
        {description}
      </p>
      <button
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-medium px-3 py-1"
      >
        <span>Créer (vitrine) et se connecter</span>
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
